import React, { Suspense } from "react";
import {
  createBrowserRouter,
  generatePath,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";
import { ResilienceProvider } from "@pulse-business-ai/resilience";
import { ProtectedRoute, CallbackPage } from "@/core/auth";
import { HubAppLayout } from "@/shared/ui/HubAppLayout";
import { ErrorBoundary } from "@/shared/ui/ErrorBoundary";
import { NotFoundPage } from "./NotFoundPage";

// Lazy-loaded feature pages
const DashboardPage = React.lazy(() => import("@/features/dashboard"));
const ActionsPage = React.lazy(() => import("@/features/actions"));
const DispatchPage = React.lazy(() => import("@/features/dispatch"));
const DispatchHistoryPage = React.lazy(() =>
  import("@/features/dispatch/DispatchHistoryPage"),
);
const PersonasPage = React.lazy(() => import("@/features/personas"));
const JobsPage = React.lazy(() => import("@/features/jobs"));
const WorkflowsPage = React.lazy(() => import("@/features/workflows"));
const WorkflowRunsPage = React.lazy(() =>
  import("@/features/workflows/RunsPage"),
);
const WorkflowRunDetailPage = React.lazy(() =>
  import("@/features/workflows/RunDetailPage"),
);
const WorkQueuePage = React.lazy(() => import("@/features/work-queue"));
const AnalyticsPage = React.lazy(() => import("@/features/analytics"));
const NotificationSettingsPage = React.lazy(() =>
  import("@/features/notifications"),
);
const ToolsPage = React.lazy(() => import("@/features/tools"));
const MemoryPage = React.lazy(() => import("@/features/memory"));
const GraphPage = React.lazy(() => import("@/features/graph"));
const SatellitesPage = React.lazy(() => import("@/features/satellites"));
const SatelliteStatusDashboard = React.lazy(() =>
  import("@/features/satellites/SatelliteStatusDashboard"),
);
const SentinelPage = React.lazy(() => import("@/features/sentinel"));
const HermesPage = React.lazy(() => import("@/features/hermes"));
const IncidentsPage = React.lazy(() => import("@/features/incidents"));
const CostsPage = React.lazy(() => import("@/features/costs"));
const CronPage = React.lazy(() => import("@/features/cron"));
const FleetSchedulesPage = React.lazy(() => import("@/features/fleet-schedules"));
const FleetReportingPage = React.lazy(() => import("@/features/fleet-reporting"));
const ChatPage = React.lazy(() => import("@/features/chat"));
const SettingsPage = React.lazy(() => import("@/features/settings"));
const AppAccessPage = React.lazy(() => import("@/features/governance"));
const McpUsersPage = React.lazy(() =>
  import("@/features/governance/McpUsers").then((m) => ({
    default: m.McpUsersPage,
  })),
);
const McpScopesPage = React.lazy(() =>
  import("@/features/governance/McpScopes").then((m) => ({
    default: m.McpScopesPage,
  })),
);
const McpActivityPage = React.lazy(() =>
  import("@/features/governance/McpActivity").then((m) => ({
    default: m.McpActivityPage,
  })),
);

function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
    </div>
  );
}

function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function RedirectRoute({ to }: { to: string }) {
  const location = useLocation();
  const params = useParams();
  const concreteParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, value ?? null]),
  );

  return (
    <Navigate
      replace
      to={{
        pathname: generatePath(to, concreteParams),
        search: location.search,
        hash: location.hash,
      }}
    />
  );
}

export const router = createBrowserRouter([
  // H8 (audit): the intermediate `/auth` SPA route is intentionally removed.
  // ProtectedRoute now bounces unauthenticated visitors cross-origin in a
  // single hop via `@/lib/auth-redirect`.
  {
    path: "/callback",
    element: <CallbackPage />,
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <ResilienceProvider>
          <HubAppLayout>
            <Outlet />
          </HubAppLayout>
        </ResilienceProvider>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <LazyRoute>
            <DashboardPage />
          </LazyRoute>
        ),
      },
      { path: "today", element: <RedirectRoute to="/" /> },
      { path: "dashboard", element: <RedirectRoute to="/" /> },

      {
        path: "inbox",
        element: (
          <LazyRoute>
            <WorkQueuePage />
          </LazyRoute>
        ),
      },
      {
        path: "inbox/actions",
        element: (
          <LazyRoute>
            <ActionsPage />
          </LazyRoute>
        ),
      },
      {
        path: "inbox/incidents",
        element: (
          <LazyRoute>
            <IncidentsPage />
          </LazyRoute>
        ),
      },
      { path: "actions", element: <RedirectRoute to="/inbox/actions" /> },
      { path: "work-queue", element: <RedirectRoute to="/inbox" /> },
      { path: "queue", element: <RedirectRoute to="/inbox" /> },
      { path: "incidents", element: <RedirectRoute to="/inbox/incidents" /> },

      {
        path: "dispatch",
        element: (
          <LazyRoute>
            <DispatchPage />
          </LazyRoute>
        ),
      },
      {
        path: "dispatch/history",
        element: (
          <LazyRoute>
            <DispatchHistoryPage />
          </LazyRoute>
        ),
      },
      {
        path: "dispatch/jobs",
        element: (
          <LazyRoute>
            <JobsPage />
          </LazyRoute>
        ),
      },
      {
        path: "dispatch/chat",
        element: (
          <LazyRoute>
            <ChatPage />
          </LazyRoute>
        ),
      },
      { path: "dispatch/jarvis", element: <RedirectRoute to="/dispatch/history" /> },
      { path: "jarvis", element: <RedirectRoute to="/dispatch/history" /> },
      { path: "jobs", element: <RedirectRoute to="/dispatch/jobs" /> },
      { path: "chat", element: <RedirectRoute to="/dispatch/chat" /> },

      {
        path: "workflows",
        element: (
          <LazyRoute>
            <WorkflowsPage />
          </LazyRoute>
        ),
      },
      {
        path: "workflows/:workflowId",
        element: (
          <LazyRoute>
            <WorkflowsPage />
          </LazyRoute>
        ),
      },
      {
        path: "workflows/runs",
        element: (
          <LazyRoute>
            <WorkflowRunsPage />
          </LazyRoute>
        ),
      },
      {
        path: "workflows/runs/:runId",
        element: (
          <LazyRoute>
            <WorkflowRunDetailPage />
          </LazyRoute>
        ),
      },
      {
        path: "scheduler",
        element: (
          <LazyRoute>
            <CronPage />
          </LazyRoute>
        ),
      },
      { path: "cron", element: <RedirectRoute to="/scheduler" /> },
      {
        path: "analytics",
        element: (
          <LazyRoute>
            <AnalyticsPage />
          </LazyRoute>
        ),
      },
      {
        path: "fleet",
        element: (
          <LazyRoute>
            <SatellitesPage />
          </LazyRoute>
        ),
      },
      {
        path: "fleet/:nodeId",
        element: (
          <LazyRoute>
            <SatellitesPage />
          </LazyRoute>
        ),
      },
      {
        path: "fleet/probes",
        element: (
          <LazyRoute>
            <SentinelPage />
          </LazyRoute>
        ),
      },
      {
        path: "fleet/diagnostics",
        element: (
          <LazyRoute>
            <SatelliteStatusDashboard />
          </LazyRoute>
        ),
      },
      { path: "fleet/status", element: <RedirectRoute to="/fleet/diagnostics" /> },
      {
        path: "fleet/signals",
        element: (
          <LazyRoute>
            <HermesPage />
          </LazyRoute>
        ),
      },
      {
        path: "fleet/schedules",
        element: (
          <LazyRoute>
            <FleetSchedulesPage />
          </LazyRoute>
        ),
      },
      {
        path: "fleet/reporting",
        element: (
          <LazyRoute>
            <FleetReportingPage />
          </LazyRoute>
        ),
      },
      { path: "satellites", element: <RedirectRoute to="/fleet" /> },
      { path: "agent-fleet", element: <RedirectRoute to="/fleet" /> },
      { path: "sentinel", element: <RedirectRoute to="/fleet/probes" /> },
      { path: "probes", element: <RedirectRoute to="/fleet/probes" /> },
      { path: "hermes", element: <RedirectRoute to="/fleet/signals" /> },

      {
        path: "tools",
        element: (
          <LazyRoute>
            <ToolsPage />
          </LazyRoute>
        ),
      },
      {
        path: "tools/:toolName",
        element: (
          <LazyRoute>
            <ToolsPage />
          </LazyRoute>
        ),
      },
      {
        path: "memory",
        element: (
          <LazyRoute>
            <MemoryPage />
          </LazyRoute>
        ),
      },
      {
        path: "graph",
        element: (
          <LazyRoute>
            <GraphPage />
          </LazyRoute>
        ),
      },
      {
        path: "graph/projects/:projectId",
        element: (
          <LazyRoute>
            <GraphPage />
          </LazyRoute>
        ),
      },
      {
        path: "personas",
        element: (
          <LazyRoute>
            <PersonasPage />
          </LazyRoute>
        ),
      },
      {
        path: "personas/:personaId",
        element: (
          <LazyRoute>
            <PersonasPage />
          </LazyRoute>
        ),
      },
      { path: "agents", element: <RedirectRoute to="/personas" /> },
      {
        path: "governance",
        element: (
          <LazyRoute>
            <AppAccessPage />
          </LazyRoute>
        ),
      },
      { path: "governance/app-access", element: <RedirectRoute to="/governance" /> },
      {
        path: "governance/mcp/users",
        element: (
          <LazyRoute>
            <McpUsersPage />
          </LazyRoute>
        ),
      },
      {
        path: "governance/mcp/scopes",
        element: (
          <LazyRoute>
            <McpScopesPage />
          </LazyRoute>
        ),
      },
      {
        path: "governance/mcp/activity",
        element: (
          <LazyRoute>
            <McpActivityPage />
          </LazyRoute>
        ),
      },
      {
        path: "settings",
        element: (
          <LazyRoute>
            <SettingsPage />
          </LazyRoute>
        ),
      },
      {
        path: "settings/notifications",
        element: (
          <LazyRoute>
            <NotificationSettingsPage />
          </LazyRoute>
        ),
      },
      {
        path: "costs",
        element: (
          <LazyRoute>
            <CostsPage />
          </LazyRoute>
        ),
      },

      // Compatibility aliases for old Admin bookmarks that now land on Hub.
      { path: "dashboard/work-queue", element: <RedirectRoute to="/inbox" /> },
      { path: "dashboard/issues", element: <RedirectRoute to="/inbox" /> },
      { path: "dashboard/analytics", element: <RedirectRoute to="/analytics" /> },
      {
        path: "dashboard/notification-settings",
        element: <RedirectRoute to="/settings/notifications" />,
      },
      { path: "dashboard/workflows", element: <RedirectRoute to="/workflows" /> },
      {
        path: "dashboard/workflows/runs",
        element: <RedirectRoute to="/workflows/runs" />,
      },
      {
        path: "dashboard/workflows/runs/:runId",
        element: <RedirectRoute to="/workflows/runs/:runId" />,
      },
      {
        path: "dashboard/workflows/:workflowId",
        element: <RedirectRoute to="/workflows/:workflowId" />,
      },
      { path: "dashboard/memory", element: <RedirectRoute to="/memory" /> },
      { path: "dashboard/graph", element: <RedirectRoute to="/graph" /> },
      {
        path: "dashboard/graph/projects/:projectId",
        element: <RedirectRoute to="/graph/projects/:projectId" />,
      },
      { path: "dashboard/scheduler", element: <RedirectRoute to="/scheduler" /> },
      { path: "dashboard/agents", element: <RedirectRoute to="/personas" /> },
      { path: "dashboard/personas", element: <RedirectRoute to="/personas" /> },
      {
        path: "dashboard/personas/:personaId",
        element: <RedirectRoute to="/personas/:personaId" />,
      },
      {
        path: "dashboard/persona-directory",
        element: <RedirectRoute to="/personas" />,
      },
      { path: "dashboard/workstreams", element: <RedirectRoute to="/dispatch/jobs" /> },
      { path: "dashboard/tools", element: <RedirectRoute to="/tools" /> },
      {
        path: "dashboard/tools/:toolName",
        element: <RedirectRoute to="/tools/:toolName" />,
      },
      {
        path: "dashboard/fleet/:nodeId",
        element: <RedirectRoute to="/fleet/:nodeId" />,
      },
      { path: "dashboard/governance", element: <RedirectRoute to="/governance" /> },
      {
        path: "admin/mcp/users",
        element: <RedirectRoute to="/governance/mcp/users" />,
      },
      {
        path: "admin/mcp/scopes",
        element: <RedirectRoute to="/governance/mcp/scopes" />,
      },
      {
        path: "admin/mcp/activity",
        element: <RedirectRoute to="/governance/mcp/activity" />,
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
